import SellingUnit from "@/components/SellingUnit";
import ShippingDetails from "@/components/checkout/ShippingDetails";
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, PackageCheck, Truck, Home, RefreshCw } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useLang, useL } from "@/i18n";
import { api, money } from "@/lib/api";

const fill = (str, vars) => Object.entries(vars).reduce((s, [k, v]) => s.replace(`{${k}}`, v), str);

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const { clear } = useCart();
  const { user } = useAuth();
  const { lang, t } = useLang();
  const L = useL();
  const [status, setStatus] = useState("checking");
  const [order, setOrder] = useState(null);
  const orderId = params.get("order_id");
  const sessionId = params.get("session_id");
  // Guest order access token (see server.py's get_order()) -- carried
  // through Stripe's hosted redirect via success_url, or straight from the
  // just-created order's own response for PayPal (see Checkout.jsx). Lets a
  // guest reach their own order below without ever being asked to log in.
  const token = params.get("token");
  const orderPath = orderId ? `/order/${orderId}${token ? `?token=${token}` : ""}` : "/account?tab=orders";

  const check = React.useCallback(async () => {
    if (!orderId) { setStatus("timeout"); return false; }
    try {
      const orderUrl = `/orders/${orderId}${token ? `?token=${token}` : ""}`;
      // Stripe's hosted Checkout only ever redirects here after the
      // customer completes payment (Back/Cancel goes to /payment/cancel
      // instead) -- but a redirect landing on this URL is still not proof
      // of payment on its own, so this asks the backend to authoritatively
      // reconcile the session with Stripe directly, instead of only
      // waiting on the async webhook to eventually do it.
      if (sessionId) {
        const { data } = await api.get(`/payments/stripe/session-status/${sessionId}?order_id=${encodeURIComponent(orderId)}`, { headers: { "X-Order-Token": token || "" } });
        if (data.status === "paid") {
          try { if (JSON.parse(sessionStorage.getItem('kb_pending_payment') || 'null')?.id === orderId) sessionStorage.removeItem('kb_pending_payment'); } catch {}
          const o = await api.get(orderUrl);
          setOrder(o.data); setStatus("paid"); clear();
          return true;
        }
        if (data.status === "expired") {
          setStatus("failed");
          return true;
        }
        // still open/processing (e.g. a delayed payment method) -- keep polling
      } else {
        // PayPal (already captured server-side before we navigated here) or
        // a Stripe webhook that beat us here.
        const o = await api.get(orderUrl);
        if (o.data.payment_status === "paid") {
          setOrder(o.data); setStatus("paid"); clear();
          return true;
        }
        if (o.data.payment_status === "payment_failed") {
          setStatus("failed");
          return true;
        }
      }
    } catch {}
    return false;
  }, [orderId, sessionId, token]); // eslint-disable-line

  useEffect(() => {
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      attempts += 1;
      const done = await check();
      if (cancelled || done) return;
      if (attempts < 8) setTimeout(poll, 1500); else setStatus("timeout");
    };
    poll();
    return () => { cancelled = true; };
  }, [check]);

  const checkAgain = () => { setStatus("checking"); check().then((done) => { if (!done) setStatus("timeout"); }); };

  return (
    <div className="mx-auto flex max-w-lg flex-col items-center px-4 py-16 sm:py-20" data-testid="payment-success-page">
      {status === "checking" && (
        <div className="w-full rounded-2xl border border-border bg-cream-card p-8 text-center card-shadow" data-testid="payment-checking">
          <Loader2 size={40} className="mx-auto mb-4 animate-spin text-gold" />
          <h1 className="font-display text-xl font-bold text-navy">{t("checkout.confirmingPayment")}</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">{t("checkout.confirmingPaymentDesc")}</p>
        </div>
      )}

      {status === "paid" && order && <OrderSuccessCard order={order} lang={lang} t={t} L={L} token={token} isGuest={!user} />}

      {status === "failed" && (
        <div className="w-full rounded-2xl border border-border bg-cream-card p-8 text-center card-shadow" data-testid="payment-failed-card">
          <XCircle size={48} className="mx-auto mb-3 text-red-500" />
          <h1 className="font-display text-2xl font-bold text-navy">{t("checkout.paymentUnsuccessfulTitle")}</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">{t("checkout.paymentUnsuccessfulDesc")}</p>
          <p className="mt-1 text-xs text-muted-foreground">{t("checkout.cartStillSaved")}</p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
            <Link to="/checkout" className="btn-gold rounded-full px-6 py-3 text-sm font-bold">{t("checkout.tryAgain")}</Link>
            <Link to="/cart" className="rounded-full border border-navy px-6 py-3 text-sm font-semibold text-navy">{t("checkout.returnToCart")}</Link>
          </div>
        </div>
      )}

      {status === "timeout" && (
        <div className="w-full rounded-2xl border border-border bg-cream-card p-8 text-center card-shadow" data-testid="payment-timeout-card">
          <Loader2 size={40} className="mx-auto mb-3 text-gold" />
          <h1 className="font-display text-xl font-bold text-navy">{t("checkout.stillConfirmingTitle")}</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">{t("checkout.stillConfirmingDesc")}</p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
            <button onClick={checkAgain} className="btn-gold flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold" data-testid="check-again-btn">
              <RefreshCw size={15} /> {t("checkout.checkAgain")}
            </button>
            <Link to={orderPath} className="rounded-full border border-navy px-6 py-3 text-sm font-semibold text-navy">{t("checkout.viewMyOrders")}</Link>
          </div>
        </div>
      )}
    </div>
  );
}

function OrderSuccessCard({ order, lang, t, L, token, isGuest }) {
  const firstName = (order.shipping_address?.full_name || "").split(" ")[0] || "";
  const addr = order.shipping_address;
  const orderPath = `/order/${order.id}${token ? `?token=${token}` : ""}`;

  return (
    <>
      <CheckCircle2 size={56} className="mb-4 text-palm-500" data-testid="payment-success-check" />
      <h1 className="text-center font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">{t("checkout.paymentSuccessfulTitle")}</h1>
      <p className="mt-2 text-center font-head text-lg font-semibold text-navy">
        {fill(t("checkout.thankYouOrder"), { name: firstName || (lang === "fr" ? "cher client" : "there") })}
      </p>
      <p className="mt-1 max-w-sm text-center text-sm text-muted-foreground">{t("checkout.orderConfirmedDesc")}</p>

      <div className="mt-5 w-full rounded-2xl border border-gold/30 bg-gold/5 px-5 py-3 text-center text-sm font-medium text-navy" data-testid="kreyola-thanks">
        {t("checkout.kreyolaThanks")}
      </div>

      <div className="mt-5 w-full space-y-4 rounded-2xl border border-border bg-cream-card p-5 text-left card-shadow sm:p-6">
        <div className="border-b border-border pb-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">{t("checkout.orderNumber")}</p>
          <p className="mb-3 font-head text-lg font-bold text-navy">{order.order_number}</p>
          <div className="grid grid-cols-2 gap-2">
            <StatusPill label={t("checkout.paymentStatusLabel")} value={t("order.status.paid")} tone="palm" icon={PackageCheck} />
            <StatusPill label={t("checkout.orderStatusLabel")} value={t(`order.status.${order.status}`)} tone="gold" icon={Truck} />
          </div>
        </div>

        <div className="grid gap-3 text-sm sm:grid-cols-2">
          <Row label={t("checkout.orderDate")} value={order.created_at ? new Date(order.created_at).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-US") : "—"} />
          <Row label={t("checkout.customerEmail")} value={order.email} />
        </div>

        <div>
          <p className="mb-2 text-xs font-semibold uppercase text-navy/50">{t("checkout.itemsPurchased")}</p>
          <div className="space-y-2">
            {(order.items || []).map((it, i) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                {it.image && <img src={it.image} alt="" className="h-11 w-11 shrink-0 rounded-lg bg-cream object-cover" />}
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-navy">{L(it, "name")}{it.selling_unit !== "marmite" && it.weight ? ` (${it.weight})` : ""}</p><SellingUnit product={it} />
                  <p className="text-xs text-muted-foreground">{money(it.price)} × {it.quantity}</p>
                </div>
                <span className="shrink-0 font-semibold text-navy">{money(it.line_total)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-1.5 border-t border-border pt-3 text-sm">
          <div className="flex justify-between text-navy/70"><span>{t("common.subtotal")}</span><span>{money(order.subtotal)}</span></div>
          <div className="flex justify-between text-navy/70"><span>{t("common.shipping")}</span><span>{order.shipping === 0 ? t("common.free") : money(order.shipping)}</span></div>
              <ShippingDetails order={order} />
          {order.discount > 0 && <div className="flex justify-between text-palm-500"><span>{t("common.discount")}{order.coupon_code ? ` (${order.coupon_code})` : ""}</span><span>-{money(order.discount)}</span></div>}
          {order.tax > 0 && <div className="flex justify-between text-navy/70"><span>{t("common.tax")}</span><span>{money(order.tax)}</span></div>}
          <div className="flex justify-between border-t border-border pt-2 text-base font-bold text-navy">
            <span>{t("checkout.amountPaid")}</span>
            <span data-testid="payment-success-total">{money(order.total)}</span>
          </div>
        </div>

        {addr && (
          <div className="border-t border-border pt-3">
            <p className="mb-1 text-xs font-semibold uppercase text-navy/50">{t("checkout.shippingTo")}</p>
            <p className="text-sm text-navy/80">
              {addr.full_name}<br />
              {addr.line1}{addr.line2 ? `, ${addr.line2}` : ""}<br />
              {addr.city}, {addr.state} {addr.zip}<br />
              {lang === "fr" ? "États-Unis" : "United States"}
            </p>
          </div>
        )}

        <Row label={t("checkout.paymentMethodLabel")} value={order.payment_method === "card" ? (lang === "fr" ? "Carte de crédit/débit" : "Credit/Debit Card") : "PayPal"} />
      </div>

      <div className="mt-5 w-full rounded-2xl border border-border bg-cream-card p-5 card-shadow">
        <p className="mb-3 text-xs font-semibold uppercase text-navy/50">{t("checkout.whatsNext")}</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <NextStep icon={CheckCircle2} label={t("checkout.stepConfirmed")} active />
          <NextStep icon={PackageCheck} label={t("checkout.stepPreparing")} />
          <NextStep icon={Truck} label={t("checkout.stepShips")} />
          <NextStep icon={Home} label={t("checkout.stepDelivered")} />
        </div>
      </div>

      <p className="mt-4 text-center text-xs text-muted-foreground">{fill(t("checkout.confirmationEmailNote"), { email: order.email })}</p>

      <div className="mt-5 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:justify-center">
        <Link to={orderPath} className="btn-gold rounded-full px-6 py-3 text-center text-sm font-bold" data-testid="view-my-order-btn">{t("checkout.viewOrder")}</Link>
        <Link to="/shop" className="rounded-full border border-navy px-6 py-3 text-center text-sm font-semibold text-navy">{t("checkout.continueShopping")}</Link>
      </div>

      {/* Optional, never a gate -- View Order above already works with no
          account at all. This is just a discoverability nudge for guests
          who might want faster access next time. */}
      {isGuest && (
        <div className="mt-5 text-center" data-testid="guest-account-nudge">
          <p className="text-xs text-muted-foreground">{t("checkout.wantEasierAccess")}</p>
          <div className="mt-2 flex items-center justify-center gap-4 text-xs font-semibold">
            <Link to="/register" className="text-ocean hover:underline">{t("checkout.createAccount")}</Link>
            <Link to="/login" className="text-ocean hover:underline">{t("checkout.signIn")}</Link>
          </div>
        </div>
      )}
    </>
  );
}

function StatusPill({ label, value, tone, icon: Icon }) {
  const toneCls = tone === "palm" ? "bg-palm-light text-palm-500" : "bg-gold/15 text-gold-600";
  return (
    <div className={`rounded-xl px-3 py-2 ${toneCls}`}>
      <p className="text-[10px] font-semibold uppercase tracking-wide opacity-70">{label}</p>
      <p className="flex items-center gap-1 text-sm font-bold"><Icon size={13} /> {value}</p>
    </div>
  );
}

function NextStep({ icon: Icon, label, active }) {
  return (
    <div className="flex flex-col items-center gap-1.5 text-center">
      <span className={`grid h-9 w-9 place-items-center rounded-full ${active ? "bg-palm-light text-palm-500" : "bg-cream text-navy/30"}`}>
        <Icon size={16} />
      </span>
      <span className={`text-[11px] font-medium leading-tight ${active ? "text-navy" : "text-navy/40"}`}>{label}</span>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="truncate font-medium text-navy">{value}</p>
    </div>
  );
}

export function PaymentCancel() {
  const { t } = useLang();
  return (
    <div className="mx-auto flex max-w-lg flex-col items-center px-4 py-16 sm:py-20" data-testid="payment-cancel-page">
      <div className="w-full rounded-2xl border border-border bg-cream-card p-8 text-center card-shadow">
        <XCircle size={48} className="mx-auto mb-3 text-sunset" />
        <h1 className="font-display text-2xl font-bold text-navy">{t("checkout.paymentNotCompletedTitle")}</h1>
        <p className="mt-1.5 text-sm text-muted-foreground">{t("checkout.cartKept")}</p>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
          <Link to="/checkout" className="btn-gold rounded-full px-6 py-3 text-sm font-bold">{t("checkout.returnToCheckout")}</Link>
          <Link to="/shop" className="rounded-full border border-navy px-6 py-3 text-sm font-semibold text-navy">{t("checkout.continueShopping")}</Link>
        </div>
      </div>
    </div>
  );
}
